import React, { useState, useEffect, useContext, useRef } from 'react';
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";

const OPERATOR_ROLE_NAMES = {
    1: "Legal Manufacturer",
    2: "Importer",
    3: "Authorised Representative",
    4: "Distributor",
    5: "Physical Manufacturer",
    9: "System or Procedure Pack Producer (SPPP)",
};

const formatOperatorRoles = (roles) => {
    if (!roles || roles.length === 0)
        return "No roles";

    return roles
        .map((roleId) => OPERATOR_ROLE_NAMES[Number(roleId)] || `Role ${roleId}`)
        .join(", ");
};

const MultiSelectField = ({ field, formState, setFormState, fieldsConfig, searchValues, setSearchValues, activeDropdown, setActiveDropdown, dropdownRefs, noneIsAll = true, itemLink = null, allowAddOperator = false, operatorRoleId = null, onRefreshOperators = null }) => {

    const [selectingDeselectingLoading, setSelectingDeselectingLoading] = useState(false);
    const [ready, setReady] = useState(false);
    const [openedOperatorAction, setOpenedOperatorAction] = useState(null);
    const [isRefreshingOperators, setIsRefreshingOperators] = useState(false);
    const [matchingOperators, setMatchingOperators] = useState([]);
    const [isLookingUpOperator, setIsLookingUpOperator] = useState(false);
    const [operatorLookupKey, setOperatorLookupKey] = useState(0);
    const clearRefreshAfterLookupRef = useRef(false);
    const dropdownItemsRef = useRef(null);

    const scrollOperatorDropdownToTop = () => {
        requestAnimationFrame(() => {
            if (dropdownItemsRef.current)
                dropdownItemsRef.current.scrollTop = 0;
        });
    };

    // Handle search input changes
    const handleSearchChange = (alias, value) => {

        setSearchValues(prev => ({ ...prev, [alias]: value }));

    };

    // Toggle dropdown visibility
    const toggleDropdown = (alias, forceOpen = false) => {
        if(!forceOpen)
            setActiveDropdown(activeDropdown === alias ? null : alias);
        else
            setActiveDropdown(alias);
    };

    // Toggle item selection
    const toggleItemSelection = (alias, itemValue) => {
        setFormState(prev => {
            const currentValues = prev[alias] || [];
            const newValues = currentValues.includes(itemValue)
            ? currentValues.filter(v => v !== itemValue)
            : [...currentValues, itemValue];
      
            return { ...prev, [alias]: newValues };
        });
        setSearchValues(prev => ({ ...prev, [alias]: '' }));
    };

    // Select all items for a field
    const selectAllItems = (alias) => {
        const field = fieldsConfig.find(f => f.Alias === alias);
        if (!field) return;

        setSelectingDeselectingLoading(true);

        setTimeout(() => {


            let currentSearch = searchValues[alias];

            setFormState(prev => {
                const newState = {
                    ...prev,
                    [alias]: field.Items.map(item => item.Value) // Add all, not filtered
                    //[alias]: field.Items.filter(item => item.Text.toLowerCase().includes(currentSearch.toLowerCase())).map(item => item.Value) // Add all, filtered
                };
    
                // This callback runs after the state update is complete
                setTimeout(() => {
                    setSelectingDeselectingLoading(false);
                }, 0);
    
                return newState;
            });

            // Close dropdown
            toggleDropdown(alias);

        }, 0);

    };

    // Deselect all items for a field
    const deselectAllItems = (alias) => {

        setSelectingDeselectingLoading(true);

        setTimeout(() => {

            setFormState(prev => {
                const newState = {
                    ...prev,
                    [alias]: []
                };
    
                // This callback runs after the state update is complete
                setTimeout(() => {
                    setSelectingDeselectingLoading(false);
                }, 0);
    
                return newState;
            });

        }, 0);


    };

    // Filter items based on search
    const getFilteredItems = (alias) => {

        const field = fieldsConfig.find(f => f.Alias === alias);
        if (!field) return [];
    
        const searchValue = (searchValues[alias] || '').trim();
        return (field.Items || []).filter(item =>
            (item.Text || '').toLowerCase().includes(searchValue.toLowerCase())
        );
    };



    
    // Click outside handler with proper cleanup
    useEffect(() => {
        const handleClickOutside = (event) => {
            let isOutside = true;
            Object.values(dropdownRefs.current).forEach(ref => {
            if (ref?.contains(event.target)) {
                isOutside = false;
            }
            });
            if (isOutside) setActiveDropdown(null);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);



    useEffect(() => {
        if (
            Object.keys(field).length > 0 &&
            Object.keys(formState).length > 0 &&
            fieldsConfig.length > 0
        )

        setReady(true);

    }, [field, formState, fieldsConfig]);


    const searchValue = ready && field?.Alias ? (searchValues[field.Alias] || "").trim() : "";
    const filteredItems = ready && field?.Alias ? getFilteredItems(field.Alias) : [];
    const showOperatorNotFound = allowAddOperator && searchValue !== "" && filteredItems.length === 0;
    const operatorRoleName = OPERATOR_ROLE_NAMES[Number(operatorRoleId)] || field?.Name || "this";
    const showOperatorRefresh = openedOperatorAction != null && openedOperatorAction.search === searchValue;

    useEffect(() => {
        if (showOperatorRefresh)
            scrollOperatorDropdownToTop();
    }, [showOperatorRefresh]);

    const hasExactNameMatch = matchingOperators.some(
        op => (op.name || "").trim().toLowerCase() === searchValue.toLowerCase()
    );
    const showAddOperator = !hasExactNameMatch;

    // When the role-filtered list has no match, look up operators by name
    // that may exist under a different role (partial name matches included).
    useEffect(() => {
        if (!showOperatorNotFound) {
            setMatchingOperators([]);
            setIsLookingUpOperator(false);
            return;
        }

        const abortController = new AbortController();
        setIsLookingUpOperator(true);
        setMatchingOperators([]);

        const timer = setTimeout(() => {
            const url = `/umbraco/api/RIMSRegistrations/Operators?page=1&search=${encodeURIComponent(searchValue)}&sortType=ASCENDING&sortBy=Establishment&returnAll=true`;

            fetch(url, { signal: abortController.signal })
                .then((response) => {
                    if (!response.ok) {
                        if (response.status === 401)
                            document.dispatchEvent(new Event("api:unauthorized"));
                        throw new Error("Network response was not ok");
                    }
                    return response.json();
                })
                .then((data) => {
                    if (abortController.signal.aborted)
                        return;

                    const operators = Array.isArray(data) ? data : [];
                    const searchLower = searchValue.toLowerCase();
                    const seenIds = new Set();

                    const matches = operators
                        .filter(op => (op.Name || "").toLowerCase().includes(searchLower))
                        .filter(op =>
                            operatorRoleId == null ||
                            !(op.Roles || []).map(Number).includes(Number(operatorRoleId))
                        )
                        .filter(op => {
                            if (op.Id == null || seenIds.has(op.Id))
                                return false;
                            seenIds.add(op.Id);
                            return true;
                        })
                        .slice(0, 10)
                        .map(op => ({
                            id: op.Id,
                            name: op.Name,
                            country: op.CountryName || "",
                            flagLink: op.FlagLink || "",
                            roles: Array.isArray(op.Roles) ? op.Roles : []
                        }));

                    setMatchingOperators(matches);
                    setIsLookingUpOperator(false);
                    if (clearRefreshAfterLookupRef.current) {
                        clearRefreshAfterLookupRef.current = false;
                        setOpenedOperatorAction(null);
                    }
                })
                .catch((error) => {
                    if (error?.name === "AbortError" || abortController.signal.aborted)
                        return;

                    setMatchingOperators([]);
                    setIsLookingUpOperator(false);
                    if (clearRefreshAfterLookupRef.current) {
                        clearRefreshAfterLookupRef.current = false;
                        setOpenedOperatorAction(null);
                    }
                });
        }, 300);

        return () => {
            clearTimeout(timer);
            abortController.abort();
        };
    }, [showOperatorNotFound, searchValue, operatorRoleId, operatorLookupKey]);

    const openAddOperator = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const params = new URLSearchParams();
        params.set("defaultOpenAddItemPopup", "true");
        params.set("operatorName", searchValue);
        if (operatorRoleId != null && operatorRoleId !== "")
            params.set("roleId", String(operatorRoleId));

        window.open(`/your-medboard/country-registrations/operators/?${params.toString()}`, "_blank", "noopener,noreferrer");
        setOpenedOperatorAction({ kind: "add", search: searchValue });
        scrollOperatorDropdownToTop();
    };

    const openEditOperator = (event, operator) => {
        event.preventDefault();
        event.stopPropagation();

        if (!operator?.id)
            return;

        window.open(
            `/your-medboard/country-registrations/operators/?operatorId=${operator.id}`,
            "_blank",
            "noopener,noreferrer"
        );
        setOpenedOperatorAction({ kind: "edit", search: searchValue, operatorId: operator.id });
        scrollOperatorDropdownToTop();
    };

    const refreshOperators = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (isRefreshingOperators || isLookingUpOperator)
            return;

        clearRefreshAfterLookupRef.current = true;
        setIsRefreshingOperators(true);
        setOperatorLookupKey((key) => key + 1);

        const refreshPromise = onRefreshOperators
            ? Promise.resolve(onRefreshOperators())
            : Promise.resolve();

        refreshPromise.finally(() => setIsRefreshingOperators(false));
    };


    return <>

        {ready ? 
            
            <div key={field.Alias}>
                <label>
                    {field.Name}{field.Mandatory ? " *" : null}
                    {(() => {
                        const selectedCount = formState[field.Alias]?.length || 0;
                        return selectedCount > 0 && <span className="selected-count">({selectedCount} selected)</span>;
                    })()}
                </label>
            
            {/* Search input and selected tags */}
            <div className="search-container">
                <input
                    type="text"
                    placeholder={formState[field.Alias].length == 0 && noneIsAll ? `All ${field.Name}` : `Add ${field.Name}...`}
                    value={searchValues[field.Alias] || ''}
                    onChange={(e) => handleSearchChange(field.Alias, e.target.value)}
                    onFocus={() => toggleDropdown(field.Alias, true)}
                    onClick={() => toggleDropdown(field.Alias, true)}
                />
              
                {/* Selected tags */}
                <div className="selected-tags">
                {formState[field.Alias]?.slice(0,10).map(value => {
                    const item = field.Items.find(i => i.Value === value);
                    return item ? (
                        <span key={value} className="tag">

                            {itemLink ?
                                <a href={`${itemLink.replaceAll("[PARAM]", value)}`} target="_blank">{item.Text}</a>
                                :
                                item.Text
                            }
                            
                            <button
                            type="button"
                            onClick={() => toggleItemSelection(field.Alias, value)}
                            className="tag-remove"
                            >
                            ×
                            </button>
                        </span>
                    ) : null;
                })}
                {
                    formState[field.Alias]?.length > 10 ? <span className="tag-remaining-items">...and {formState[field.Alias]?.length - 10} more.</span> : null
                }
                </div>
            </div>
            
            {/* Dropdown with items */}
            {activeDropdown === field.Alias && (
                <div
                    ref={el => dropdownRefs.current[field.Alias] = el}
                    className="dropdown-container">

                <div className="dropdown-actions">
                    {
                        selectingDeselectingLoading ? <LoadingSpinnerImage /> : <>
                            <button
                                style={{display: "none"}}
                                type="button"
                                onClick={() => selectAllItems(field.Alias)}
                                className="select-all"
                                >
                                Select All
                            </button>
                            <button
                                style={{display: "none"}}
                                type="button"
                                onClick={() => deselectAllItems(field.Alias)}
                                className="deselect-all"
                                >
                                Deselect All
                            </button>
                        </>
                    }
                </div>

                <div className="dropdown-items" ref={dropdownItemsRef}>
                    {filteredItems.map(item => (
                    <div
                        key={item.Value}
                        className={`dropdown-item ${formState[field.Alias]?.includes(item.Value) ? 'selected' : ''}`}
                        onClick={() => toggleItemSelection(field.Alias, item.Value)}
                    >
                        <input
                        type="checkbox"
                        checked={formState[field.Alias]?.includes(item.Value) || false}
                        readOnly
                        />
                        {item.Text}
                    </div>
                    ))}
                    {showOperatorRefresh ? (
                        <div
                            className="operator-refresh-row"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                className="operator-refresh-button"
                                title="Refresh search"
                                disabled={isRefreshingOperators || isLookingUpOperator}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={refreshOperators}
                            >
                                <i className={`fas fa-sync-alt${isRefreshingOperators || isLookingUpOperator ? " fa-spin" : ""}`}></i>
                                Refresh
                            </button>
                        </div>
                    ) : null}

                    {showOperatorNotFound ? (
                        <>
                            {isLookingUpOperator ? (
                                <div
                                    className="dropdown-item operator-not-found"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <span className="operator-not-found-label">Checking operators...</span>
                                </div>
                            ) : (
                                <>
                                    {showAddOperator ? (
                                        <div
                                            className="dropdown-item operator-not-found"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <span className="operator-not-found-label">
                                                {matchingOperators.length === 0
                                                    ? `"${searchValue}" not found`
                                                    : `Create "${searchValue}" as a new operator`}
                                            </span>
                                            <button
                                                type="button"
                                                className="operator-add-button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={openAddOperator}
                                            >
                                                Add {searchValue}
                                            </button>
                                        </div>
                                    ) : null}

                                    {matchingOperators.length > 0 ? (
                                        <>
                                            <div
                                                className={`operator-role-missing-separator${showAddOperator ? "" : " operator-role-missing-separator--first"}`}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                The operators below were found but don't have the {operatorRoleName} role
                                            </div>

                                            {matchingOperators.map(operator => (
                                                <div
                                                    key={operator.id}
                                                    className="dropdown-item operator-not-found"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <span className="operator-not-found-label">
                                                        <span className="operator-match-name">
                                                            {operator.flagLink ? (
                                                                <img
                                                                    className="operator-match-flag"
                                                                    src={operator.flagLink}
                                                                    alt={operator.country || ""}
                                                                    title={operator.country || ""}
                                                                />
                                                            ) : null}
                                                            {operator.name}
                                                        </span>
                                                        <span className="operator-match-meta">
                                                            {formatOperatorRoles(operator.roles)}
                                                        </span>
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="operator-add-button"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={(e) => openEditOperator(e, operator)}
                                                    >
                                                        Edit
                                                    </button>
                                                </div>
                                            ))}
                                        </>
                                    ) : null}
                                </>
                            )}
                        </>
                    ) : null}
                </div>
                </div>
            )}
            </div>

        : <div>Loading...</div>}

    </>


}

export default MultiSelectField;